import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, LogOut, User } from 'lucide-react';
import { UserAvatar } from './UserAvatar';
import { useAuth } from '../lib/AuthContext';

interface HeaderUserMenuProps {
  /** Bump na avatar-upload om opnieuw te laden */
  avatarVersion?: number;
}

export function HeaderUserMenu({ avatarVersion = 0 }: HeaderUserMenuProps) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  if (!user?.authenticated || user.session_kind !== 'user') return null;

  const label = user.display_name?.trim() || user.email?.split('@')[0] || 'Account';
  const sub = user.email ?? '';

  const titleAttr = sub ? `${label} — ${sub}` : label;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        title={titleAttr}
        aria-label={titleAttr}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-lg border border-border/80 bg-surface/80 px-1.5 py-1 text-left transition hover:border-primary/40 hover:bg-surface-card xl:max-w-[14rem] xl:gap-2 xl:px-2 xl:py-1.5"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <UserAvatar
          displayName={user.display_name}
          email={user.email}
          hasAvatar={user.has_avatar}
          size={34}
          imageVersion={avatarVersion}
        />
        <span className="min-w-0 flex-1 max-xl:hidden">
          <span className="block truncate text-sm font-medium text-text">{label}</span>
          {sub && <span className="block truncate text-[11px] text-text-muted">{sub}</span>}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-text-muted transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className="absolute right-0 z-[60] mt-1 min-w-[13rem] rounded-xl border border-border bg-surface-card py-1 shadow-lg"
          role="menu"
        >
          <Link
            to="/account"
            role="menuitem"
            className="flex items-center gap-2 px-3 py-2 text-sm text-text hover:bg-surface"
            onClick={() => setOpen(false)}
          >
            <User className="h-4 w-4 text-text-muted" />
            Mijn account
          </Link>
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text hover:bg-surface"
            onClick={() => {
              setOpen(false);
              void logout();
            }}
          >
            <LogOut className="h-4 w-4 text-text-muted" />
            Uitloggen
          </button>
        </div>
      )}
    </div>
  );
}
